import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Typography,
  useTheme,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import SubscriptionPlanSelector from "./SubscriptionPlanSelector";
import MoyasarPaymentWireframe from "./MoyasarPaymentWireframe";
import { checkoutApi } from "../services/checkout-service";
import {
  getFullAccessPlans,
  getInferenceOnlyPlans,
  useUsagePlans,
} from "../services/usage-plan-service";
import {
  clearActiveCheckout,
  isZeroAmount,
  readActiveCheckout,
  saveActiveCheckout,
} from "../utils/active-checkout";
const SHOW_PAYMENT_UI_IN_STEP_TWO = false;
const DEFAULT_SALES_EMAIL = "sales@omniops.sa";
const SALES_EMAIL =
  window.env?.REACT_APP_SALES_CONTACT_EMAIL?.trim() || DEFAULT_SALES_EMAIL;

const OrganizationForm = ({
  open,
  onClose,
  onSubmit,
  organization = null,
  loading = false,
  restoreReason = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [subscriptionSelection, setSubscriptionSelection] = useState({
    planId: null,
    selectedPlanCode: null,
  });
  const [step, setStep] = useState("details");
  const [activeCheckout, setActiveCheckout] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentAttempt, setPaymentAttempt] = useState(0);
  const checkoutRequestInFlightRef = useRef(false);
  const checkoutAbortRef = useRef(null);
  const usagePlansQuery = useUsagePlans(!organization && open);
  const usagePlans = usagePlansQuery.data ?? [];
  const inferenceOnlyPlans = useMemo(
    () => getInferenceOnlyPlans(usagePlans),
    [usagePlans],
  );
  const fullAccessPlans = useMemo(
    () => getFullAccessPlans(usagePlans),
    [usagePlans],
  );

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || "",
        slug: organization.slug || "",
        description: organization.description || "",
      });
      setActiveCheckout(null);
      setSubscriptionSelection({
        planId: null,
        selectedPlanCode: null,
      });
      setStep("details");
    } else {
      const storedCheckout = readActiveCheckout();
      setFormData({
        name: "",
        slug: "",
        description: "",
      });
      if (storedCheckout?.paymentSubmitted) {
        setActiveCheckout(storedCheckout);
        setSubscriptionSelection((previous) => ({
          ...previous,
          planId: null,
          selectedPlanCode: storedCheckout.planCode,
        }));
        setStep("payment");
      } else {
        if (storedCheckout) clearActiveCheckout();
        setActiveCheckout(null);
        setSubscriptionSelection({
          planId: null,
          selectedPlanCode: null,
        });
        setStep("details");
      }
    }
    setErrors({});
    setApiError("");
    setPaymentFailed(false);
  }, [organization]);

  useEffect(() => {
    if (
      organization ||
      usagePlansQuery.isLoading ||
      usagePlansQuery.isError ||
      !usagePlansQuery.data
    ) {
      return;
    }

    if (subscriptionSelection.selectedPlanCode) {
      const selectedPlan = usagePlans.find(
        (plan) =>
          plan.code === subscriptionSelection.selectedPlanCode &&
          plan.is_active,
      );
      const isSelectableInferencePlan = inferenceOnlyPlans.some(
        (plan) => plan.code === subscriptionSelection.selectedPlanCode,
      );
      const isSelectableFullAccessPlan = fullAccessPlans.some(
        (plan) => plan.code === subscriptionSelection.selectedPlanCode,
      );

      if (
        !selectedPlan ||
        (!isSelectableInferencePlan && !isSelectableFullAccessPlan)
      ) {
        setApiError(t("subscriptionPlans.selectedPlanUnavailable"));
        setSubscriptionSelection((current) => ({
          ...current,
          planId: null,
          selectedPlanCode: null,
        }));
        return;
      }

      const restoredAccessMode = selectedPlan.is_inference_only
        ? "inference_only"
        : "full_access";
      if (subscriptionSelection.planId !== restoredAccessMode) {
        setSubscriptionSelection((current) => ({
          ...current,
          planId: restoredAccessMode,
        }));
      }
      return;
    }

    if (subscriptionSelection.planId === "inference_only") {
      setSubscriptionSelection((current) => {
        if (inferenceOnlyPlans.length > 0) {
          return current;
        }
        if (!inferenceOnlyPlans.length) {
          return { ...current, planId: null, selectedPlanCode: null };
        }
        return current;
      });
    }
  }, [
    fullAccessPlans,
    inferenceOnlyPlans,
    organization,
    subscriptionSelection.planId,
    subscriptionSelection.selectedPlanCode,
    t,
    usagePlans,
    usagePlansQuery.data,
    usagePlansQuery.isError,
    usagePlansQuery.isLoading,
  ]);

  useEffect(() => {
    if (restoreReason === "failed") {
      setApiError(t("moyasarPayment.paymentFailed"));
      setPaymentFailed(true);
    } else if (restoreReason === "cancelled") {
      setApiError(t("moyasarPayment.paymentCancelled"));
      setPaymentFailed(true);
    } else if (restoreReason === "expired") {
      setApiError(t("moyasarPayment.checkoutExpired"));
      setPaymentFailed(false);
    }
  }, [restoreReason, t]);

  useEffect(
    () => () => {
      checkoutAbortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    const shouldWarnBeforeLeaving =
      SHOW_PAYMENT_UI_IN_STEP_TWO &&
      open &&
      !organization &&
      step === "payment" &&
      activeCheckout &&
      !activeCheckout.paymentSubmitted;

    if (!shouldWarnBeforeLeaving) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [activeCheckout, open, organization, step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear API error when user starts typing
    if (apiError) {
      setApiError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("organizationForm.nameRequired");
    }

    if (!formData.slug.trim()) {
      newErrors.slug = t("organizationForm.slugRequired");
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = t("organizationForm.slugPattern");
    } else if (formData.slug.length < 3) {
      newErrors.slug = t("organizationForm.slugMin");
    } else if (formData.slug.length > 50) {
      newErrors.slug = t("organizationForm.slugMax");
    }

    if (formData.name.length > 100) {
      newErrors.name = t("organizationForm.nameMax");
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = t("organizationForm.descriptionMax");
    }

    if (!organization && !canContinue) {
      newErrors.subscriptionPlan = t("organizationForm.planRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubscriptionChange = (selection) => {
    setApiError("");
    setErrors((current) => ({ ...current, subscriptionPlan: "" }));
    setSubscriptionSelection(selection);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    if (!organization) {
      if (checkoutRequestInFlightRef.current) return;
      if (!canContinue || !subscriptionSelection.selectedPlanCode) {
        setApiError(t("subscriptionPlans.selectValidPlan"));
        return;
      }

      const requestPayload = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        ...(formData.description.trim()
          ? { description: formData.description.trim() }
          : {}),
        plan_code: subscriptionSelection.selectedPlanCode,
      };

      checkoutRequestInFlightRef.current = true;
      setCheckoutLoading(true);
      setApiError("");
      checkoutAbortRef.current = new AbortController();

      try {
        const response = await checkoutApi.createCheckout(
          requestPayload,
          checkoutAbortRef.current.signal,
        );

        if (
          typeof response?.session_id !== "string" ||
          !response.session_id.trim() ||
          typeof response?.action !== "string" ||
          typeof response?.amount !== "string"
        ) {
          throw new Error("invalid_checkout_response");
        }

        // Zero values such as "0E-9" are recognized numerically. The current
        // payment behavior remains unchanged and success is still backend-verified.
        void isZeroAmount(response.amount);

        const checkoutState = {
          sessionId: response.session_id,
          planCode: requestPayload.plan_code,
          action: response.action,
          currentStep: "payment",
          paymentSubmitted: false,
        };

        if (!saveActiveCheckout(checkoutState)) {
          throw new Error("checkout_storage_unavailable");
        }

        setActiveCheckout(checkoutState);
        setPaymentFailed(false);
        setStep("payment");
      } catch (error) {
        if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
          handleSubmitError(error);
        }
      } finally {
        checkoutRequestInFlightRef.current = false;
        checkoutAbortRef.current = null;
        setCheckoutLoading(false);
      }
      return;
    }

    try {
      await submitOrganization(formData);
    } catch (error) {
      handleSubmitError(error);
    }
  };

  const handleSubmitError = useCallback(
    (error) => {
      let errorMessage = t("organizationForm.unexpectedError");

      if (
        error.message === "invalid_checkout_response" ||
        error.message === "checkout_storage_unavailable"
      ) {
        errorMessage = t("moyasarPayment.checkoutUnavailable");
      } else if (error.response?.status === 409) {
        errorMessage = t("organizationForm.duplicateSlug");
      } else if (
        error.response?.status === 400 ||
        error.response?.status === 422
      ) {
        errorMessage = t("organizationForm.invalidData");
      } else if (error.response?.status === 401) {
        errorMessage = t("moyasarPayment.authenticationRequired");
      } else if (error.response?.status === 403) {
        errorMessage = t("organizationForm.permissionDenied");
      } else if (error.response?.status >= 500) {
        errorMessage = t("moyasarPayment.checkoutUnavailable");
      }

      setApiError(errorMessage);
    },
    [t],
  );

  const submitOrganization = useCallback(
    async (payload) => {
      await onSubmit(payload);
    },
    [onSubmit],
  );

  const handlePaymentCompleted = useCallback(() => {
    // Moyasar redirects to the callback page; only the backend checkout status
    // can complete organization creation.
    setApiError("");
    if (activeCheckout) {
      const submittedCheckout = {
        ...activeCheckout,
        paymentSubmitted: true,
      };
      saveActiveCheckout(submittedCheckout);
      setActiveCheckout(submittedCheckout);
    }
  }, [activeCheckout]);

  const handlePaymentFailed = useCallback(() => {
    setApiError(t("moyasarPayment.paymentFailed"));
    setPaymentFailed(true);
  }, [t]);
  const handleCopySessionId = useCallback(async () => {
    if (!activeCheckout?.sessionId) return;

    try {
      await navigator.clipboard.writeText(activeCheckout.sessionId);
      toast.success(t("moyasarPayment.sessionIdCopied"));
    } catch {
      toast.error(t("moyasarPayment.sessionIdCopyFailed"));
    }
  }, [activeCheckout?.sessionId, t]);

  const handleRetryPayment = () => {
    setApiError("");
    setPaymentFailed(false);
    setPaymentAttempt((attempt) => attempt + 1);
  };

  const handleBackToDetails = () => {
    if (!loading && !checkoutLoading) {
      clearActiveCheckout();
      setActiveCheckout(null);
      setStep("details");
    }
  };

  const handleClose = () => {
    if (!loading && !checkoutLoading) {
      if (!organization && activeCheckout) {
        clearActiveCheckout();
        setActiveCheckout(null);
      }
      setFormData({ name: "", slug: "", description: "" });
      setErrors({});
      setApiError("");
      setSubscriptionSelection({
        planId: null,
        selectedPlanCode: null,
      });
      setStep("details");
      setPaymentFailed(false);
      setPaymentAttempt(0);
      onClose();
    }
  };

  const isBusy = loading || checkoutLoading;
  const selectedPlan = usagePlans.find(
    (plan) =>
      plan.code === subscriptionSelection.selectedPlanCode && plan.is_active,
  );
  const canContinue =
    !usagePlansQuery.isLoading &&
    !usagePlansQuery.isError &&
    (subscriptionSelection.planId === "inference_only"
      ? Boolean(
          selectedPlan?.is_inference_only &&
          inferenceOnlyPlans.some((plan) => plan.code === selectedPlan.code),
        )
      : subscriptionSelection.planId === "full_access"
        ? fullAccessPlans.some(
            (plan) => plan.code === subscriptionSelection.selectedPlanCode,
          )
        : false);

  const fieldBorderColor = isDarkMode ? "rgba(148, 163, 184, 0.24)" : "#D8E2DE";
  const fieldBackgroundColor = isDarkMode ? "#071011" : "#FFFFFF";
  const mutedTextColor = isDarkMode ? "#AFC2BA" : "#52635E";
  const formLabelSx = {
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
    fontWeight: 500,
    mb: 0.75,
    "& .required": {
      color: theme.palette.primary.main,
    },
    "& .optional": {
      color: theme.palette.primary.main,
    },
  };
  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      bgcolor: fieldBackgroundColor,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: fieldBorderColor,
      },
      "&:hover fieldset": {
        borderColor: isDarkMode ? "rgba(226, 232, 240, 0.42)" : "#B8C9C2",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      py: 1.35,
      fontSize: "0.875rem",
      "&::placeholder": {
        color: mutedTextColor,
        opacity: 0.7,
      },
    },
    "& .MuiOutlinedInput-inputMultiline": {
      py: 0,
    },
    "& .MuiFormHelperText-root": {
      mx: 0,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 640,
          borderRadius: "10px",
          bgcolor: theme.palette.background.paper,
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ display: "none" }}>
        {organization
          ? t("organizations.editOrganization")
          : t("organizationForm.createTitle")}
      </DialogTitle>

      <>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          {step === "details" ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 2 }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                {t("organizationForm.organizationInformation")}
              </Typography>

              <Box>
                <Typography
                  component="label"
                  htmlFor="organization-name"
                  sx={formLabelSx}
                >
                  {t("organizationForm.nameShortLabel")}{" "}
                  <Box component="span" className="required">
                    *
                  </Box>
                </Typography>
                <TextField
                  id="organization-name"
                  name="name"
                  placeholder={t("organizationForm.namePlaceholder")}
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isBusy}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  htmlFor="organization-slug"
                  sx={formLabelSx}
                >
                  {t("organizationForm.slugShortLabel")}{" "}
                  <Box component="span" className="required">
                    *
                  </Box>
                </Typography>
                <TextField
                  id="organization-slug"
                  name="slug"
                  placeholder={t("organizationForm.slugPlaceholder")}
                  value={formData.slug}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.slug}
                  helperText={errors.slug}
                  disabled={isBusy}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  htmlFor="organization-description"
                  sx={formLabelSx}
                >
                  {t("organizationForm.descriptionLabel")}{" "}
                  <Box component="span" className="optional">
                    ({t("organizationForm.optional")})
                  </Box>
                </Typography>
                <TextField
                  id="organization-description"
                  name="description"
                  placeholder={t("organizationForm.descriptionPlaceholder")}
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description}
                  disabled={isBusy}
                  sx={textFieldSx}
                />
              </Box>

              {!organization && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    mt: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                  >
                    {t("organizationForm.subscription")}{" "}
                    <Box
                      component="span"
                      className="required"
                      sx={{ color: "primary.main" }}
                    >
                      *
                    </Box>
                  </Typography>
                  <SubscriptionPlanSelector
                    value={subscriptionSelection}
                    onChange={handleSubscriptionChange}
                    disabled={isBusy}
                    usagePlans={usagePlans}
                    plansLoading={usagePlansQuery.isLoading}
                    plansError={usagePlansQuery.isError}
                    onRetryPlans={usagePlansQuery.refetch}
                  />
                  {errors.subscriptionPlan && (
                    <Typography variant="caption" color="error">
                      {errors.subscriptionPlan}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : SHOW_PAYMENT_UI_IN_STEP_TWO ? (
            activeCheckout && selectedPlan ? (
              <MoyasarPaymentWireframe
                key={`${activeCheckout.sessionId}-${paymentAttempt}`}
                organizationName={formData.name}
                checkout={activeCheckout}
                planName={selectedPlan.name}
                planPrice={selectedPlan.price}
                onPaymentCompleted={handlePaymentCompleted}
                onPaymentFailed={handlePaymentFailed}
              />
            ) : activeCheckout && usagePlansQuery.isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : activeCheckout ? (
              <Alert
                severity="error"
                action={
                  usagePlansQuery.isError ? (
                    <Button
                      color="inherit"
                      onClick={() => usagePlansQuery.refetch()}
                    >
                      {t("common.retry")}
                    </Button>
                  ) : null
                }
              >
                {t("subscriptionPlans.selectedPlanUnavailable")}
              </Alert>
            ) : null
          ) : activeCheckout ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                py: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("moyasarPayment.sessionIdLabel")}
                </Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: fieldBorderColor,
                    borderRadius: "8px",
                    bgcolor: fieldBackgroundColor,
                  }}
                >
                  <Typography
                    component="code"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                    }}
                  >
                    {activeCheckout.sessionId}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleCopySessionId}
                    aria-label={t("moyasarPayment.copySessionId")}
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopyRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Alert
                severity="info"
                sx={{
                  alignItems: "flex-start",
                  "& .MuiAlert-icon": { mt: "2px", mr: 1.5, py: 0 },
                  "& .MuiAlert-message": { width: "100%", py: 0 },
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("moyasarPayment.salesSetupTitle")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {t("moyasarPayment.salesSetupIntro")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("moyasarPayment.salesReferenceInstruction")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("moyasarPayment.salesCompletionInstruction")}
                </Typography>
              </Alert>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 1.5,
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: fieldBorderColor,
                    borderRadius: "8px",
                    bgcolor: fieldBackgroundColor,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.primary"
                    fontWeight={700}
                  >
                    {t("moyasarPayment.salesEmail")}
                  </Typography>
                  <Box
                    component="a"
                    href={`mailto:${SALES_EMAIL}`}
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: isDarkMode
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      overflowWrap: "anywhere",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {SALES_EMAIL}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2.5, gap: 1, justifyContent: "flex-end" }}
        >
          {SHOW_PAYMENT_UI_IN_STEP_TWO && step === "payment" && (
            <Button
              onClick={handleBackToDetails}
              disabled={isBusy}
              color="secondary"
              variant="text"
            >
              {t("common.back")}
            </Button>
          )}
          {SHOW_PAYMENT_UI_IN_STEP_TWO &&
            step === "payment" &&
            paymentFailed && (
              <Button
                onClick={handleRetryPayment}
                disabled={isBusy}
                variant="contained"
                color="primary"
              >
                {t("moyasarPayment.retryPayment")}
              </Button>
            )}
          <Button
            onClick={handleClose}
            disabled={isBusy}
            color="secondary"
            variant="outlined"
            sx={{
              borderRadius: "8px",
              px: 2,
              color: theme.palette.text.primary,
              borderColor: fieldBorderColor,
              textTransform: "none",
            }}
          >
            {t(step === "payment" ? "common.close" : "common.cancel")}
          </Button>
          {step !== "payment" && (
            <Button
              type="button"
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isBusy || (!organization && !canContinue)}
              startIcon={isBusy ? <CircularProgress size={20} /> : null}
              sx={{
                borderRadius: "8px",
                px: 2.5,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {isBusy
                ? t("organizationForm.saving")
                : organization
                  ? t("organizationForm.update")
                  : t("common.continue")}
            </Button>
          )}
        </DialogActions>
      </>
    </Dialog>
  );
};

export default OrganizationForm;
